import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { PromotionalCard } from '@atlassian/jira-home-common-ui';
import { ExternalLinkWithAnalytics } from '@atlassian/jira-home-common-ui/src/common/ui/external-link';
import { useIntl } from '@atlassian/jira-intl';
import type { main_homeBroadcastWidget_Widget$key } from '@atlassian/jira-relay/src/__generated__/main_homeBroadcastWidget_Widget.graphql';
import decorativeImage from '../assets/advanced-roadmap-broadcast-image.svg';
import { WIDGET_ID, JIRA_SOFTWARE_UPGRADE_TO_PREMIUM } from '../constants';
import messages from './messages';
import { useDismissBroadcast } from './use-dismiss-broadcast';

export interface WidgetProps {
	queryReference: main_homeBroadcastWidget_Widget$key;
}

export const Widget = ({ queryReference }: WidgetProps) => {
	const { formatMessage } = useIntl();

	const { dismiss, isDismissLoading } = useDismissBroadcast();

	const broadcastData = useFragment<main_homeBroadcastWidget_Widget$key>(
		graphql`
			fragment main_homeBroadcastWidget_Widget on Query {
				jira @required(action: THROW) @optIn(to: ["JiraBroadcastMessage"]) {
					allJiraUserBroadcastMessages(cloudId: $cloudId) {
						edges {
							node {
								shouldDisplay
								id
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const broadcastMessages = useMemo(() => {
		if (broadcastData?.jira?.allJiraUserBroadcastMessages?.edges) {
			const { edges } = broadcastData.jira.allJiraUserBroadcastMessages;

			if (Array.isArray(edges) && edges.length) {
				return edges.filter((edge) => {
					if (edge?.node) {
						const { shouldDisplay } = edge.node ?? {};

						return Boolean(shouldDisplay);
					}

					return false;
				});
			}
		}

		return [];
	}, [broadcastData]);

	if (!broadcastMessages.length) {
		return null;
	}

	const tryPremiumButtonClickedEvent = {
		href: JIRA_SOFTWARE_UPGRADE_TO_PREMIUM,
		actionSubjectId: 'yourWorkUpsellTryPremiumButton',
		attributes: {
			namespaces: 'navigationNextScreen.jiraHomeScreen.jiraHomeScreen',
		},
	};

	return (
		<>
			{broadcastMessages.map(({ node }) => (
				<PromotionalCard
					key={node?.id}
					header={formatMessage(messages.header)}
					description={formatMessage(messages.cardDescription)}
					primaryAction={
						<ExternalLinkWithAnalytics {...tryPremiumButtonClickedEvent}>
							{formatMessage(messages.ctaLink)}
						</ExternalLinkWithAnalytics>
					}
					reason={formatMessage(messages.footerTooltip)}
					analyticsSource={WIDGET_ID}
					isDismissLoading={isDismissLoading}
					onDismiss={() => dismiss(node?.id)}
					imageUrl={decorativeImage}
					imageAltText=""
				/>
			))}
		</>
	);
};
